import { useState } from "react";
import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard,
  Building2,
  Users,
  RefreshCw,
  CreditCard,
  LogOut,
  Shield,
  Menu,
  X,
  Mail,
} from "lucide-react";

const navItems = [
  { icon: LayoutDashboard, label: "Overview", path: "/admin" },
  { icon: Building2, label: "Organizaciones", path: "/admin/organizations" },
  { icon: Users, label: "Usuarios", path: "/admin/users" },
  { icon: RefreshCw, label: "Sync Logs", path: "/admin/sync-logs" },
  { icon: CreditCard, label: "Planes", path: "/admin/plans" },
  { icon: Mail, label: "Email", path: "/admin/email" },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!user || !["super_admin", "support_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[210px] bg-white border-r border-[#E8E8E5] transform transition-transform lg:translate-x-0 lg:static lg:inset-auto flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2 px-5 py-[18px] border-b border-[#F0F0ED]">
          <div className="w-[22px] h-[22px] bg-[#0A0A0A] rounded-[4px] flex items-center justify-center">
            <Shield className="w-3 h-3 text-white" strokeWidth={2.5} />
          </div>
          <span
            className="text-[#0A0A0A]"
            style={{ fontFamily: "Chivo", fontWeight: 900, fontSize: "14px", letterSpacing: "-0.03em" }}
          >
            Admin Panel
          </span>
        </div>
        <nav className="flex-1 py-3 space-y-[2px]">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== "/admin" && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`relative flex items-center gap-3 pl-5 pr-3 py-2 text-[13px] transition-colors ${
                  isActive
                    ? "text-[#0A0A0A] font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:bg-[#C8192C] before:rounded-r-sm"
                    : "text-[#888888] hover:text-[#0A0A0A]"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-[15px] h-[15px]" strokeWidth={1.75} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-5 py-3 border-t border-[#F0F0ED]">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-[#888888] hover:text-[#0A0A0A] text-[12px] transition-colors"
            data-testid="admin-back-to-app"
          >
            <LogOut className="w-3.5 h-3.5" />
            Volver a la app
          </Link>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E8E8E5] px-6 py-3 flex items-center justify-between lg:justify-end">
          <button className="lg:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2 text-[12px] text-[#888888]">
            <Shield className="w-3.5 h-3.5 text-[#C8192C]" />
            <span>{user.email}</span>
            <span className="px-2 py-0.5 border border-[#E8E8E5] text-[#404040] text-[10.5px] font-medium uppercase tracking-wider">
              {user.role}
            </span>
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
